import { Inject, Injectable } from '@angular/core';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { Observable, combineLatest, map, share } from 'rxjs';

import { CurrencyPipe } from '../../../../../../base/pipes/currency.pipe';
import { flagEnumContainsValue } from '../../../../../../base/utils/flag-enum-contains-value-utils';
import { IPlaceBetButtonMessages } from '../../../../models';
import { IPlaceButtonState, PlaceButtonStates } from '../../../../state';
import { combineTotalStakes } from '../../../stake/stake-utils';
import { IPlaceButtonMessagesProvider, PLACE_BUTTON_MESSAGES_PROVIDER } from '../../place-button-messages-provider.service';
import { IPlaceButtonStateService, IPlaceButtonTitleService, PLACE_BUTTON_STATE_SERVICE } from '../interfaces/place-button-interfaces';

@Injectable()
export class BetslipPlaceButtonTitleService implements IPlaceButtonTitleService {
    buttonTitle$: Observable<string>;

    constructor(
        @Inject(PLACE_BUTTON_STATE_SERVICE) placeButtonService: IPlaceButtonStateService,
        @Inject(PLACE_BUTTON_MESSAGES_PROVIDER) messagesProvider: IPlaceButtonMessagesProvider,
        private currencyPipe: CurrencyPipe,
        private epcotConfig: EpcotConfig,
    ) {
        const placeButtonState$ = placeButtonService.state$;

        const messages$ = messagesProvider.messages$;

        this.buttonTitle$ = combineLatest([placeButtonState$, messages$]).pipe(
            map(([state, messages]) => {
                const title = this.getButtonTitle(state, messages);

                return this.transformTitle(title, state);
            }),
            share(),
        );
    }

    private getButtonTitle(state: IPlaceButtonState, messages: IPlaceBetButtonMessages): string {
        if (!state.action) {
            return '';
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.UpdateStake)) {
            return messages.updateMinStake;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.UpdateStakes)) {
            return messages.updateMinStakes;
        }

        if (state.isProcessing) {
            // eslint-disable-next-line no-bitwise
            if (flagEnumContainsValue(state.action, PlaceButtonStates.PlaceBet | PlaceButtonStates.Deposit)) {
                return messages.depositAndBetProcessing;
            }

            if (flagEnumContainsValue(state.action, PlaceButtonStates.Deposit)) {
                return messages.depositProcessing;
            }

            return messages.placeProcessing;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.PlaceBet)) {
            if (flagEnumContainsValue(state.action, PlaceButtonStates.AcceptChanges)) {
                if (state.isFreeBet) {
                    return messages.acceptAndPlaceFreeBet;
                }

                return messages.acceptAndPlaceBet;
            }

            if (flagEnumContainsValue(state.action, PlaceButtonStates.Deposit)) {
                return messages.depositAndPlaceBet;
            }

            if (state.isFreeBet) {
                return messages.placeFreeBet;
            }

            return messages.placeBet;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.Deposit)) {
            return messages.makeADeposit;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.AcceptChanges)) {
            return messages.acceptChanges;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.Login)) {
            return messages.loginToBet;
        }

        if (flagEnumContainsValue(state.action, PlaceButtonStates.Confirm)) {
            return messages.confirm;
        }

        return messages.placeBet;
    }

    protected transformTitle(title: string, state: IPlaceButtonState): string {
        const showStake = state.isLinear || !this.epcotConfig.isEnabled;
        const isValidButtonState = state.isValid && flagEnumContainsValue(state.action, PlaceButtonStates.PlaceBet);

        if (!showStake || !isValidButtonState) {
            return title;
        }

        const totalStake = combineTotalStakes(state.stakes);

        const displayStake = (totalStake.freeBetAmount ?? 0) + (totalStake.stake ?? 0);

        if (!displayStake) {
            return title;
        }

        const formattedStake = this.currencyPipe.transform(displayStake, true);

        return `${title} ${formattedStake}`;
    }
}
